/* Common Imports */

import { styled, useMediaQuery, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";
import {
	createNewBroadcastThunk,
	fetchFeatureDiscoveryTrackerThunk,
	setBroadcastFormThunk,
} from "../../../redux-magic/store";
import { saveBroadcastDraftThunk } from "../../../redux-magic/thunks";

/* Component Imports */

import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from "@mui/material";
import {
	BroadcastPricingPayload,
	broadcastNeedsPayment,
	fetchBroadcastPricing,
	payForBroadcast,
} from "../../../lib/broadcasts/broadcastCheckout";
import Commission from "./Commission";
import AgentScreen from "./NoOfAgentScreen";
import BroadcastLocationPicker, { BROADCAST_CITIES, BroadcastTargetLocation } from "./BroadcastLocationPicker";
import Notes from "./Notes";
import SuccessScreen from "./SuccessScreen";
import Summary from "./Summary";

/* Icon Imports */

import { ArrowBack, Close, SendOutlined } from "@mui/icons-material";
import BeegruButton from "../../common-components/buttons/BeegruButton";
import Toutorial from "./Tutorial";

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		/* 2K breakpoint  2560 ~~4 cards start~~ */
		[theme.breakpoints.up(theme.breakpoints.values.xl + 640)]: {
			minHeight: "55rem",
			maxHeight: "55rem",
		},
		/* 1080p breakpoint */
		[theme.breakpoints.up("xl")]: {
			minHeight: "55rem",
			maxHeight: "55rem",
		},
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
			maxHeight: "100%",
		},
		minHeight: "40rem",
		maxHeight: "40rem",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
		position: "relative",
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	fontSize: "1.25rem",
	fontWeight: 500,
	lineHeight: "106%",
	background: theme.palette.background.paper,
	gap: "0rem",
	padding: "1rem ",
	borderRadius: "1rem 1rem 0rem 0rem",
	"&+.MuiDialogContent-root": {
		padding: "0px",
	},
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "0.5rem",
	padding: "0rem 0rem 0rem 0rem",
	background: theme.palette.background.paper,
	overflow: "scroll",
	flex: 1,
	scrollbarWidth: "none",
	// Remove default MUI padding
	"&.MuiDialogContent-root": {
		padding: "0px",
		height: "100%",
	},
	// Remove padding from subsequent dialog content
	"&+.MuiDialogContent-root": {
		padding: "0px",
	},
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
	padding: "1rem",
	background: theme.palette.background.paper,
	borderRadius: "1rem 1rem 1rem 1rem",
	"& > :not(:first-of-type)": {
		marginLeft: 0,
	},
}));

const RowContainer = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
});

const TitleText = styled(Typography)({
	fontWeight: 600,
});

export default function CreateNewBroadcastModal({
	dispatch,
	broadcast,
	transactionType,
	propertyId,
	business_profile_id,
	session,
	profile_context,
	propertyStatus,
	setOpen,
	open,
	router,
	source,
	handleCloseCreateModal,
	broadcastFeatureDiscoveryTracker,
	draftBroadcastId,
	draftForm,
	onDraftChanged,
}: {
	business_profile_id: string | ObjectId | undefined | null;
	dispatch: Function;
	broadcast: StateInterface["broadcast_data"] | undefined | null;
	transactionType: string;
	propertyId: ObjectId | string;
	session: StateInterface["redux_session"];
	profile_context: StateInterface["profile_context"];
	propertyStatus: string;
	setOpen: Function;
	open: boolean;
	source: { url: string; component: string }; // source can be an object with url and component or just a string
	router: NextRouter;
	handleCloseCreateModal?: () => void;
	broadcastFeatureDiscoveryTracker: StateInterface["feature_discovery_tracker"]["broadcast"];
	/* Continue editing a draft (the app's drafts): the dialog opens on the preview with the draft's
	   values, a close saves back into that draft, and Send publishes it in place. */
	draftBroadcastId?: string;
	draftForm?: { no_of_agents: number; commission_offered: number; notes: string };
	/* A draft was saved or discarded, or a broadcast sent: the list should reload. */
	onDraftChanged?: () => void;
}) {
	const theme = useTheme();

	const smDevice: boolean = useMediaQuery(theme.breakpoints.down("sm"));

	/* Translations */

	const localLang = {
		skip: router.locale === "ar-AE" ? "تخطي" : router.locale === "kn-IN" ? "ಬಿಟ್ಟುಹೋಗಿ" : "Skip",
		next: router.locale === "ar-AE" ? "التالي" : router.locale === "kn-IN" ? "ಮುಂದೆ" : "Next",
		network_of_agents:
			router.locale === "ar-AE" ? "شبكة الوكلاء" : router.locale === "kn-IN" ? "ಏಜೆಂಟ್‌ಗಳ ಜಾಲ" : "Network of Agents",
		brokerage: router.locale === "ar-AE" ? "الوساطة" : router.locale === "kn-IN" ? "ಮಧ್ಯಸ್ಥಿಕೆ" : "Brokerage",
		notes_optional:
			router.locale === "ar-AE"
				? "ملاحظات (اختياري)"
				: router.locale === "kn-IN"
					? "ಸೂಚನೆಗಳು (ಐಚ್ಛಿಕ)"
					: "Notes (Optional)",
		preview: router.locale === "ar-AE" ? "معاينة" : router.locale === "kn-IN" ? "ಪೂರ್ವವೀಕ್ಷಣೆ" : "Preview",
		select_brokerage:
			router.locale === "ar-AE"
				? "اختر الوساطة"
				: router.locale === "kn-IN"
					? "ಮಧ್ಯಸ್ಥಿಕೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ"
					: "Select brokerage",
		confirm: router.locale === "ar-AE" ? "تأكيد" : router.locale === "kn-IN" ? "ದೃಢೀಕರಿಸಿ" : "Confirm",
		send_broadcast:
			router.locale === "ar-AE" ? "إرسال البث" : router.locale === "kn-IN" ? "ಪ್ರಸಾರವನ್ನು ಕಳುಹಿಸಿ" : "Send Broadcast",
		done: router.locale === "ar-AE" ? "تم" : router.locale === "kn-IN" ? "ಮುಗಿಯಿತು" : "Done",
		watchTutorial:
			router.locale === "ar-AE"
				? "مشاهدة البرنامج التعليمي"
				: router.locale === "kn-IN"
					? "ಟ್ಯುಟೋರಿಯಲ್ ವೀಕ್ಷಿಸಿ"
					: "Watch Tutorial",
		pay_and_send:
			router.locale === "ar-AE" ? "ادفع وأرسل" : router.locale === "kn-IN" ? "ಪಾವತಿಸಿ ಮತ್ತು ಕಳುಹಿಸಿ" : "Pay & Send",
		send_failed:
			router.locale === "ar-AE"
				? "تعذر إرسال البث. حاول مرة أخرى."
				: router.locale === "kn-IN"
					? "ಪ್ರಸಾರವನ್ನು ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
					: "Couldn't send the broadcast. Please try again.",
	};

	const [page, setPage] = React.useState<number>(draftBroadcastId ? 5 : 1);
	const [previewPage, setPreviewPage] = React.useState<boolean>(Boolean(draftBroadcastId));

	React.useEffect(() => {
		if (open && draftBroadcastId && draftForm) {
			dispatch(setBroadcastFormThunk(draftForm));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, draftBroadcastId]);

	const [sendLoading, setSendLoading] = React.useState<boolean>(false);

	/* Paid broadcasts (`broadcast.payment_enabled` in feature_pricing_per_market,
	   lib/broadcasts/broadcastCheckout.ts): the price for the agents picked, the error from the last
	   Send, and a payment already verified for this broadcast, so a retry after a failed send never
	   charges twice (the server's claim is idempotent for the same payment). */
	const [pricing, setPricing] = React.useState<BroadcastPricingPayload | null>(null);
	const [paymentError, setPaymentError] = React.useState<string>("");
	const [paidPaymentId, setPaidPaymentId] = React.useState<string>("");
	/* Who it goes to, on a paid broadcast (BroadcastLocationPicker); Near by Areas at 5 km to start, as in the app. */
	const [targetLocation, setTargetLocation] = React.useState<BroadcastTargetLocation>({ scope: "near_by", radius_km: 5 });

	/* A broadcast to a city (4 Nov 2026): its reach, bands and price are that city's (lib/broadcasts/broadcastReach.ts),
	   on the agents step, the price and the order alike. */
	const broadcastCity: string | null = targetLocation.scope === "entire_city" ? targetLocation.city : null;

	// Re-read whenever the band or the city picked changes: the server prices it (fee, offer and GST included), says
	// whether this business's broadcast is free (store billing, 1 Nov 2026), and which cities may be picked.
	React.useEffect(() => {
		if (open)
			fetchBroadcastPricing(
				business_profile_id ? String(business_profile_id) : "",
				broadcast?.no_of_agents || 0,
				broadcastCity,
				BROADCAST_CITIES,
			).then(setPricing);
	}, [open, broadcast?.no_of_agents, business_profile_id, broadcastCity]);

	React.useEffect(() => {
		dispatch(
			fetchFeatureDiscoveryTrackerThunk({
				profile_id: profile_context.is_business_profile ? profile_context.business_profile_id : profile_context.user_id,
				is_business_profile: profile_context.is_business_profile,
			}),
		);

		// on close or browser action, go back to page 2 if viewed
		const handlePopState = (event: PopStateEvent) => {
			event.preventDefault();
			setPage(broadcastFeatureDiscoveryTracker.viewed && page <= 2 ? 2 : page - 1);
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (broadcastFeatureDiscoveryTracker && !draftBroadcastId) {
			if (broadcastFeatureDiscoveryTracker.viewed) {
				setPage(2);
			}
		}
	}, [broadcastFeatureDiscoveryTracker, draftBroadcastId]);

	type CompProps = {
		dispatch: Function;
		broadcast: StateInterface["broadcast_data"] | undefined | null;
		transactionType: string;
		propertyId: ObjectId | string;
		userId: string | ObjectId;
		business_profile_id: string | ObjectId | undefined | null;
		close: Function;
		setPage: Function;
		session: StateInterface["redux_session"];
		profile_context: any;
		propertyStatus: string;
		router: NextRouter;
		loading: boolean;
		broadcastFeatureDiscoveryTracker: StateInterface["feature_discovery_tracker"]["broadcast"];
		pricing?: BroadcastPricingPayload | null;
		paymentError?: string;
		city?: string | null;
	};

	let Comp: React.ComponentType<CompProps> = () => (
		<div
			style={{
				padding: "0rem",
				width: "100%",
				height: "100%",
			}}
		></div>
	);

	let ButtonLabel = "";

	let title = "";

	switch (page) {
		case 1:
			Comp = Toutorial;
			ButtonLabel = localLang.next;
			title = localLang.network_of_agents;
			break;
		case 2:
			Comp = AgentScreen;
			ButtonLabel = localLang.select_brokerage;
			title = localLang.network_of_agents;
			break;
		case 3:
			Comp = Commission;
			ButtonLabel = localLang.confirm;
			title = localLang.select_brokerage;
			break;
		case 4:
			Comp = Notes;
			ButtonLabel = localLang.next;
			title = localLang.notes_optional;
			break;
		case 5:
			Comp = Summary;
			ButtonLabel = broadcastNeedsPayment(pricing) && !paidPaymentId ? localLang.pay_and_send : localLang.send_broadcast;
			title = localLang.preview;
			break;
		case 6:
			Comp = SuccessScreen;
			ButtonLabel = localLang.done;
			title = "Success";
			break;
		default:
			Comp = Toutorial;
	}

	const handleClose = () => {
		/* Closed part-way (agents picked, not yet sent): keep it as a draft, as the app's Save & Exit
		   does. Saved into the draft it was resumed from, else upserted per listing by the server. */
		if (page >= 3 && page <= 5 && !sendLoading) {
			dispatch(
				saveBroadcastDraftThunk({
					source: source,
					creator_is_business_profile: true,
					creator_user_id: session?.user?.dbUser?._id || "",
					creator_business_profile_id: business_profile_id ? business_profile_id : "",
					property_id: propertyId,
					no_of_agents: broadcast?.no_of_agents || 0,
					commission_offered: broadcast?.commission_offered || 0,
					notes: broadcast?.notes || "",
					property_status: propertyStatus,
					...(draftBroadcastId ? { broadcast_id: draftBroadcastId } : {}),
				}),
			).then((saved: { ok: boolean }) => {
				if (saved?.ok && onDraftChanged) onDraftChanged();
			});
		}
		setOpen(false);
		setPage(2);
		setPaymentError("");
		setPaidPaymentId("");
		dispatch(
			setBroadcastFormThunk({
				commission_offered: transactionType === "rent" ? 15 : 2,
				no_of_agents: broadcast?.no_of_agents || 0,
				notes: "",
			}),
		);
		if (handleCloseCreateModal) {
			handleCloseCreateModal();
		}
	};

	const handleClick = async () => {
		// if (page === 2) setPage(1);
		if (previewPage && page < 5) setPage(5);
		else if (page !== 6) {
			if (page === 5) {
				setSendLoading(true);
				setPaymentError("");

				/* While broadcast payments are on, pay first (read again now: the switch may have
				   flipped since the dialog opened). A payment verified on an earlier try is reused. */
				let razorpayPaymentId: string = paidPaymentId;
				if (!razorpayPaymentId) {
					const livePricing = await fetchBroadcastPricing(
						business_profile_id ? String(business_profile_id) : "",
						broadcast?.no_of_agents || 0,
						broadcastCity,
					);
					setPricing(livePricing);
					if (broadcastNeedsPayment(livePricing)) {
						const dbUser: any = session?.user?.dbUser;
						const paid = await payForBroadcast({
							businessProfileId: business_profile_id ? String(business_profile_id) : "",
							noOfAgents: broadcast?.no_of_agents || 0,
							prefill: {
								name: [dbUser?.first_name, dbUser?.last_name].filter(Boolean).join(" "),
								email: dbUser?.recovery_email?.email || "",
								contact: dbUser?.phone ? `${dbUser?.country_code || ""}${dbUser.phone}` : "",
							},
							themeColor: "#621b16",
							city: broadcastCity,
						});
						if (!paid.ok) {
							setSendLoading(false);
							setPaymentError(paid.cancelled ? "" : paid.error);
							return;
						}
						razorpayPaymentId = paid.paymentId;
						setPaidPaymentId(paid.paymentId);
					}
				}

				const result = await dispatch(
					createNewBroadcastThunk({
						source: source,
						creator_is_business_profile: true,
						creator_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
						creator_business_profile_id: business_profile_id ? business_profile_id : "",
						property_id: propertyId,
						no_of_agents: broadcast?.no_of_agents || 0,
						commission_offered: broadcast?.commission_offered || 0,
						notes: broadcast?.notes || "",
						property_status: propertyStatus,
						sessionId: session ? (session.session_id ? session.session_id : "") : "",
						profile_context: profile_context,
						...(razorpayPaymentId ? { razorpay_payment_id: razorpayPaymentId } : {}),
						...(draftBroadcastId ? { broadcast_id: draftBroadcastId } : {}),
						...(broadcastNeedsPayment(pricing) ? { target_location: targetLocation } : {}),
					}),
				);
				setSendLoading(false);

				/* Stay on the preview with the reason when the send failed (it used to show Success anyway). */
				if (!result?.ok) {
					setPaymentError(result?.error || localLang.send_failed);
					return;
				}
				setPaidPaymentId("");
				if (onDraftChanged) onDraftChanged();
			}
			// if (page === 4) setPreviewPage(true);
			setPage(page + 1);
		} else {
			handleClose();
			handleCloseCreateModal ? handleCloseCreateModal() : null;
		}
	};

	const handleClickSecondary = async () => {
		if (page === 2) {
			setPage(1);
		} else {
			setPage(5);
			setPreviewPage(true);
		}
	};

	return (
		<CustomDialog
			maxWidth="xs"
			fullWidth={true}
			fullScreen={smDevice}
			open={open}
			onClose={handleClose}
		>
			{page !== 6 ? (
				<CustomDialogTitle>
					<RowContainer
						sx={{
							gap: "0.5rem",
						}}
					>
						{page !== 2 && page !== 6 && !sendLoading ? (
							<IconButton
								aria-label="back"
								onClick={() => {
									setPage(page - 1);
								}}
							>
								<ArrowBack fontSize="small" />
							</IconButton>
						) : null}
						<TitleText variant="body1">{title}</TitleText>
					</RowContainer>

					{!sendLoading ? (
						<IconButton
							aria-label="close"
							onClick={handleClose}
						>
							<Close fontSize="small" />
						</IconButton>
					) : null}
				</CustomDialogTitle>
			) : null}

			<CustomDialogContent>
				<Comp
					broadcastFeatureDiscoveryTracker={broadcastFeatureDiscoveryTracker}
					propertyStatus={propertyStatus}
					close={handleClose}
					dispatch={dispatch}
					broadcast={broadcast}
					transactionType={transactionType}
					propertyId={propertyId}
					business_profile_id={business_profile_id}
					userId={profile_context.user_id}
					setPage={setPage}
					session={session}
					profile_context={profile_context}
					router={router}
					loading={sendLoading}
					pricing={pricing}
					paymentError={paymentError}
					city={broadcastCity}
				/>
				{page === 2 && broadcastNeedsPayment(pricing) ? (
					<BroadcastLocationPicker
						value={targetLocation}
						onChange={setTargetLocation}
						locale={router?.locale}
						cities={pricing?.cities || []}
					/>
				) : null}
			</CustomDialogContent>

			<CustomDialogActions>
				{[4, 2].includes(page) ? (
					<BeegruButton
						size="large"
						flavor="primary"
						variant="text"
						disabled={sendLoading}
						fullWidth
						disableRipple
						disableFocusRipple
						disableTouchRipple
						onClick={handleClickSecondary}
						sx={{
							"&.MuiButton-text": {
								color: "#621b16",
								background: "none",
								"&:hover": {
									background: "none",
								},
								"&:active": {
									background: "none",
								},
								"&:focus": {
									background: "none",
								},
							},
						}}
					>
						{page === 2 ? localLang.watchTutorial : localLang.skip}
					</BeegruButton>
				) : null}

				{page !== 1 && (
					<BeegruButton
						size="large"
						flavor="primary"
						variant="contained"
						disabled={sendLoading}
						fullWidth
						disableRipple
						disableFocusRipple
						disableTouchRipple
						onClick={handleClick}
						sx={{
							"&.MuiButton-contained": {
								background: "#621b16",
								color: "#FFFFFF",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								"&:hover": {
									background: "#621b16",
									boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								},
								"&:active": {
									background: "#621b16",
									boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								},
								"&:focus": {
									background: "#621b16",
									boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
								},
							},
						}}
					>
						{!sendLoading ? (
							<>
								{ButtonLabel}

								{page === 5 ? (
									<SendOutlined
										sx={{
											fontSize: "1rem",
											margin: "0rem 0rem 0rem 0.5rem",
										}}
									/>
								) : null}
							</>
						) : (
							<CircularProgress
								sx={{
									color: "#FFFFFF",
								}}
							/>
						)}
					</BeegruButton>
				)}
			</CustomDialogActions>
		</CustomDialog>
	);
}
